import React from 'react';
import styled from 'styled-components';
import ErrorCard from '../ErrorCard';

const Container = styled.div`
  margin: 32px 0;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CheckIcon = styled.svg`
  width: 14px;
  height: 14px;
  color: var(--card-dot);
`;

const DetailsToggle = styled.div`
  width: 100%;
  padding: 14px 0 0 0;
  border: none;
  background: transparent;
  color: ${(props) => props.$isSelected ? "rgba(255,255,255,0.8)" : "var(--card-muted)"};
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 18px;
  cursor: pointer;
  border-top: 1px solid var(--card-divider);
  opacity: ${(props) => props.$isSelected ? 0.9 : 0.7};
  transition: all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
  
  &:hover {
    opacity: 1;
    color: ${(props) => props.$isSelected ? "#ffffff" : "var(--zyvgo-primary, #222222)"};
  }
`;

const ChevronDown = styled.svg`
  width: 14px;
  height: 14px;
  transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
  transform: ${(props) => props.$isExpanded ? "rotate(180deg)" : "rotate(0deg)"};
`;

const OptionBlock = styled.div`
  position: relative;
  cursor: pointer;
  
  input[type="radio"],
  input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
    appearance: none;
    -webkit-appearance: none;
    pointer-events: none;
  }
`;

const OptionLabel = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border: 2px solid ${props => props.$isSelected ? 'var(--zyvgo-primary, #222222)' : 'var(--zyvgo-border, #e1e5e9)'};
  border-radius: var(--zyvgo-radius-md, 8px);
  background-color: ${props => props.$isSelected ? 'var(--zyvgo-primary, #222222)' : '#FFFFFF'};
  color: ${props => props.$isSelected ? '#FFFFFF' : 'var(--zyvgo-text-primary, #222222)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  font-weight: ${props => props.$isSelected ? '600' : '500'};
  
  &:hover {
    border-color: var(--zyvgo-primary, #222222);
    ${props => !props.$isSelected && `
      background-color: rgba(34, 34, 34, 0.02);
    `}
  }
`;

const OptionText = styled.span`
  flex: 1;
  min-width: 0;
`;

const PriceContainer = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 16px;
  flex-shrink: 0;
`;

const SuppliesList = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dotted var(--card-divider);
`;

const SuppliesHeading = styled.div`
  font-size: 0.68rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--card-muted);
  margin-bottom: 8px;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.5;
  }
`;

const SupplyRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  line-height: 1.4;
  padding: 8px 0;
  border-bottom: 1px solid var(--card-divider);

  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SupplyLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const SupplyName = styled.span`
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--card-text);
`;

const SupplyMeta = styled.span`
  font-size: 0.75rem;
  text-align: right;
  color: var(--card-muted);
  font-weight: 500;
`;

const SupplyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 3px 8px;
  color: ${(props) => (props.$included ? "var(--badge-inc-text)" : "var(--badge-add-text)")};
  background: ${(props) => (props.$included ? "var(--badge-inc-bg)" : "var(--badge-add-bg)")};
  border: 1px solid ${(props) => (props.$included ? "var(--badge-inc-border)" : "var(--badge-add-border)")};
`;

const ProductCard = styled.label`
  /* Theme bridge for dark mode selection state */
  --card-bg: ${(props) => props.$isSelected ? "var(--zyvgo-primary, #222222)" : "var(--zyvgo-card-bg, #ffffff)"};
  --card-border: ${(props) => props.$isSelected ? "var(--zyvgo-primary, #222222)" : "var(--zyvgo-border, #d7dee8)"};
  --card-text: ${(props) => props.$isSelected ? "#ffffff" : "var(--zyvgo-text-primary, #0f172a)"};
  --card-muted: ${(props) => props.$isSelected ? "rgba(255, 255, 255, 0.7)" : "var(--zyvgo-text-muted, #64748b)"};
  --card-divider: ${(props) => props.$isSelected ? "rgba(255, 255, 255, 0.15)" : "rgba(148, 163, 184, 0.1)"};
  --card-dot: ${(props) => props.$isSelected ? "#ffffff" : "var(--zyvgo-primary, #222222)"};
  
  /* Backgrounds logic for badges */
  --badge-inc-bg: ${(props) => props.$isSelected ? "rgba(255, 255, 255, 0.15)" : "rgba(22, 163, 74, 0.08)"};
  --badge-inc-text: ${(props) => props.$isSelected ? "#ffffff" : "#166534"};
  --badge-inc-border: ${(props) => props.$isSelected ? "rgba(255, 255, 255, 0.2)" : "rgba(22, 163, 74, 0.15)"};

  --badge-add-bg: ${(props) => props.$isSelected ? "rgba(255, 255, 255, 0.05)" : "rgba(34, 34, 34, 0.05)"};
  --badge-add-text: ${(props) => props.$isSelected ? "rgba(255, 255, 255, 0.9)" : "var(--zyvgo-primary, #222222)"};
  --badge-add-border: ${(props) => props.$isSelected ? "rgba(255, 255, 255, 0.1)" : "rgba(34, 34, 34, 0.12)"};

  display: block;
  border: 1px solid var(--card-border);
  border-radius: 18px;
  background: var(--card-bg);
  color: var(--card-text);
  padding: 22px 24px;
  box-shadow: ${(props) => props.$isSelected ? "0 10px 40px rgba(15, 23, 42, 0.12)" : "0 2px 8px rgba(15, 23, 42, 0.04)"};
  transition: all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
  cursor: pointer;

  &:hover {
    border-color: var(--zyvgo-primary, #222222);
  }
`;

const SelectionIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid ${(props) => props.$isSelected ? "transparent" : "var(--zyvgo-border, #d7dee8)"};
  background: ${(props) => props.$isSelected ? "var(--zyvgo-primary, #222222)" : "transparent"};
  box-shadow: ${(props) => props.$isSelected ? "0 4px 12px rgba(0,0,0,0.15)" : "none"};
  flex-shrink: 0;
  transition: all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);

  & > svg {
    color: #ffffff;
    opacity: ${(props) => props.$isSelected ? 1 : 0};
    transform: scale(${(props) => props.$isSelected ? 1 : 0.5});
    transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const ProductMain = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const ProductImageWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 14px;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(148, 163, 184, 0.08);
  border: 1px solid var(--card-divider);
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ProductTitleWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductTitle = styled.div`
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: var(--card-text);
`;

const ProductSub = styled.div`
  font-size: 0.78rem;
  color: var(--card-muted);
  margin-top: 3px;
`;

const ProductPriceWrap = styled.div`
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ProductPrice = styled.div`
  font-size: 1.65rem;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--card-text);
`;

const ProgramGroupCard = styled.div`
  border: 1px solid var(--zyvgo-border, #d7dee8);
  border-radius: 10px;
  background: var(--zyvgo-card-bg, #ffffff);
  padding: 18px 16px 14px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const TreatmentGroupCard = styled.div`
  border: 1px solid var(--zyvgo-border, #d7dee8);
  border-radius: 14px;
  background: var(--zyvgo-card-bg, #ffffff);
  padding: 18px 16px 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
`;

const TreatmentGroupHeader = styled.div`
  margin-bottom: 14px;
`;

const TreatmentGroupEyebrow = styled.div`
  font-size: 0.72rem;
  font-weight: 800;
  color: var(--zyvgo-primary, #008b7a);
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TreatmentGroupTitle = styled.div`
  margin-top: 8px;
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--zyvgo-text-primary, #111827);
  line-height: 1.2;
`;

const TreatmentProductsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProgramGroupEyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  color: var(--zyvgo-primary, #008b7a);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const ProgramGroupTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--zyvgo-text-primary, #111827);
  line-height: 1.25;
`;

const ProgramGroupSub = styled.div`
  font-size: 0.82rem;
  color: var(--zyvgo-text-muted, #64748b);
  margin-top: 4px;
`;

const ProgramGroupPrompt = styled.div`
  margin-top: 18px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--zyvgo-text-muted, #7c8798);
`;

const ProgramOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-top: 8px;
`;

const ProgramOptionButton = styled.button`
  position: relative;
  width: 100%;
  border: 2px solid ${(props) => props.$isSelected ? "var(--zyvgo-primary, #008b7a)" : "var(--zyvgo-border, #d7dee8)"};
  border-radius: 7px;
  background: ${(props) => props.$isSelected ? "rgba(0, 139, 122, 0.09)" : "#ffffff"};
  color: var(--zyvgo-text-primary, #111827);
  padding: 12px 12px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: var(--zyvgo-primary, #008b7a);
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
  }
`;

const ProgramOptionTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 0.92rem;
`;

const ProgramOptionDot = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${(props) => props.$isSelected ? "var(--zyvgo-primary, #008b7a)" : "var(--zyvgo-border, #d7dee8)"};
  background: ${(props) => props.$isSelected ? "var(--zyvgo-primary, #008b7a)" : "#ffffff"};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 16px;

  &::after {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #ffffff;
    opacity: ${(props) => props.$isSelected ? 1 : 0};
  }
`;

const ProgramOptionPrice = styled.div`
  margin-top: 8px;
  font-weight: 900;
  font-size: 1.05rem;
  color: var(--zyvgo-text-primary, #111827);
`;

const ProgramOptionMeta = styled.div`
  margin-top: 3px;
  font-size: 0.72rem;
  color: var(--zyvgo-text-muted, #64748b);
`;

const ProgramOptionBadge = styled.div`
  position: absolute;
  top: -10px;
  right: 10px;
  border-radius: 4px;
  background: var(--zyvgo-primary, #008b7a);
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.03em;
  padding: 3px 7px;
`;



const ReceiptContainer = styled.div`
  margin-top: 24px;
  border-top: 1px solid var(--card-divider);
  padding-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;
    
const ReceiptRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  font-weight: ${(props) => (props.$isTotal ? "800" : "500")};
  color: var(--card-text);
  opacity: ${(props) => (props.$isTotal ? "1" : "0.85")};
  border-top: ${(props) => (props.$isTotal ? "1px solid var(--card-divider)" : "none")};
  padding-top: ${(props) => (props.$isTotal ? "16px" : "0")};
  margin-top: ${(props) => (props.$isTotal ? "6px" : "0")};
  
  ${(props) => props.$isTotal && `
    font-size: 1.1rem;
    letter-spacing: -0.01em;
  `}
`;

const ReceiptLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ReceiptLabel = styled.span`
  line-height: 1.2;
`;

const ReceiptMeta = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.7;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ReceiptRight = styled.div`
  text-align: right;
  white-space: nowrap;
`;

const ReceiptOriginalPrice = styled.div`
  font-size: 0.72rem;
  text-decoration: line-through;
  opacity: 0.6;
  font-weight: 500;
  margin-bottom: 2px;
`;

const formatPrice = (val) => {
  if (val === null || val === undefined) return null;
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num) || num < 0 || num > 50000) return null;
  return `$${num.toFixed(2)}`;
};

const SingleChoice = ({ question, value, onChange, error, isTreatmentJourney = false }) => {
  const [expandedCards, setExpandedCards] = React.useState({});
  const [imageLoadFailures, setImageLoadFailures] = React.useState({});
  // The grouped/multi-product renderer belongs to program journeys only.
  // Legacy questionnaires keep the production single-choice product card.
  const isMultiProductSelection = isTreatmentJourney && question?.question_type === 'product_selection';

  const toggleCard = (e, choiceValue) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedCards(prev => ({
      ...prev,
      [choiceValue]: !prev[choiceValue]
    }));
  };

  const handleChange = (selectedValue) => {
    // Always trigger onChange, even if the same value is selected again
    // This ensures the auto-advance logic works when user goes back and re-selects
    onChange(selectedValue);
  };

  const handleLabelClick = (e, choice) => {
    e.preventDefault();
    if (isMultiProductSelection) {
      const nextValue = toggleMultiSelection(choice);
      handleChange(nextValue);
      return;
    }
    const isSelected = isChoiceSelected(choice);
    if (isSelected) {
      handleChange('');
    } else {
      handleChange(choice);
    }
  };

  // Helper to get display text from choice (handles both strings and objects)
  const getChoiceDisplay = (choice) => {
    if (typeof choice === 'string') {
      return choice;
    }
    if (choice?.label) {
      return choice.label;
    }
    // For product objects, show product_name
    return choice.product_name || choice.name || JSON.stringify(choice);
  };

  // Helper to get choice value for comparison (handles both strings and objects)
  const getChoiceValue = (choice) => {
    if (typeof choice === 'string') {
      return choice;
    }
    if (choice?.value || choice?.label) {
      return choice.value || choice.label;
    }
    // For product objects, use product_id as the value
    return choice.product_id || choice.id || JSON.stringify(choice);
  };

  // Helper to check if a choice is selected
  const isChoiceSelected = (choice) => {
    const choiceValue = getChoiceValue(choice);
    if (isMultiProductSelection) {
      const current = Array.isArray(value) ? value : [];
      return current.some((item) => {
        const itemValue = typeof item === 'object' && item !== null
          ? (item.product_id || item.id || item.value || item.label)
          : item;
        return String(itemValue) === String(choiceValue);
      });
    }
    // Handle both string values and object values
    if (typeof value === 'string') {
      return value === choiceValue;
    }
    if (typeof value === 'object' && value !== null) {
      return (value.product_id || value.id || value.value || value.label) === choiceValue;
    }
    return false;
  };

  const toggleMultiSelection = (choice) => {
    const choiceValue = getChoiceValue(choice);
    const current = Array.isArray(value) ? [...value] : [];
    const exists = current.some((item) => {
      const itemValue = typeof item === 'object' && item !== null
        ? (item.product_id || item.id || item.value || item.label)
        : item;
      return String(itemValue) === String(choiceValue);
    });

    if (exists) {
      return current.filter((item) => {
        const itemValue = typeof item === 'object' && item !== null
          ? (item.product_id || item.id || item.value || item.label)
          : item;
        return String(itemValue) !== String(choiceValue);
      });
    }

    return [...current, choice];
  };

  const selectProductProgram = (choice, groupChoices) => {
    const choiceValue = getChoiceValue(choice);
    const groupValues = new Set(groupChoices.map((item) => String(getChoiceValue(item))));
    const current = Array.isArray(value) ? [...value] : [];
    const alreadySelected = current.some((item) => {
      const itemValue = typeof item === 'object' && item !== null
        ? (item.product_id || item.id || item.value || item.label)
        : item;
      return String(itemValue) === String(choiceValue);
    });
    const withoutGroup = current.filter((item) => {
      const itemValue = typeof item === 'object' && item !== null
        ? (item.product_id || item.id || item.value || item.label)
        : item;
      return !groupValues.has(String(itemValue));
    });

    return alreadySelected ? withoutGroup : [...withoutGroup, choice];
  };

  const getSelectedChoiceInGroup = (groupChoices) => (
    groupChoices.find((choice) => isChoiceSelected(choice)) || null
  );

  // Validate a price value is numeric and within bounds
  const validatePrice = (val) =>
    !isNaN(Number(val)) && Number(val) >= 0 && Number(val) <= 50000 ? Number(val) : undefined;

  // Check if choice has pricing info (product_selection with enriched data)
  const getChoicePricing = (choice) => {
    if (typeof choice !== 'object' || choice === null) return null;

    const finalPrice = validatePrice(choice.final_price) ?? validatePrice(choice.total);
    const originalPrice = validatePrice(choice.original_price);
    const shippingFee = validatePrice(choice.shipping_fee) ?? 0;

    if (finalPrice === undefined && originalPrice === undefined) return null;

    return {
      finalPrice,
      originalPrice,
      hasDiscount: originalPrice !== undefined && finalPrice !== undefined && originalPrice > finalPrice,
      shippingFee,
      total: validatePrice(choice.total),
    };
  };

  const getLinkedSupplies = (choice) => {
    if (!choice || typeof choice !== 'object') return [];
    if (!Array.isArray(choice.linked_supplies)) return [];
    return choice.linked_supplies;
  };

  const getChoiceImage = (choice) => {
    if (!choice || typeof choice !== 'object') return '';
    const rawImage = choice.product_image || choice.image || '';
    if (!rawImage || typeof rawImage !== 'string') return '';
    if (/^https?:\/\//i.test(rawImage) || rawImage.startsWith('data:')) {
      return rawImage;
    }

    const clientApiEndpoint = window.localStorage.getItem('welliemd_client_api_endpoint') || '';
    if (!clientApiEndpoint) return rawImage;

    try {
      const apiUrl = new URL(clientApiEndpoint);
      return new URL(rawImage, apiUrl.origin).toString();
    } catch (_error) {
      return rawImage;
    }
  };

  const getSuppliesAddonsTotal = (supplies) => {
    if (!Array.isArray(supplies)) return 0;
    return supplies.reduce((sum, s) => {
      if (Boolean(s?.is_included)) return sum;
      const qty = Number(s?.quantity || 1);
      const unit = Number(s?.unit_price || 0);
      return sum + (Number.isFinite(qty) && Number.isFinite(unit) ? qty * unit : 0);
    }, 0);
  };

  const visibleChoices = Array.isArray(question.answer_choices)
    ? question.answer_choices.filter((choice) => !(choice && typeof choice === 'object' && choice.is_active === false))
    : [];

  const groupedChoiceRows = (() => {
    if (!isMultiProductSelection) {
      return visibleChoices.map((choice, index) => ({ type: 'single', choice, key: `single_${index}` }));
    }

    const treatmentGroups = new Map();
    const rows = [];

    visibleChoices.forEach((choice, index) => {
      const isProductCard = typeof choice === 'object' && choice !== null && Boolean(choice.product_id || choice.id);
      if (!isProductCard) {
        rows.push({ type: 'single', choice, key: `single_${index}` });
        return;
      }

      const treatmentKeySource =
        choice?.treatment_type_key ||
        choice?.treatment_type_name ||
        choice?.treatment ||
        choice?.category_name ||
        choice?.__sourceQuestionText ||
        'treatment';
      const treatmentKey = String(treatmentKeySource).trim().toLowerCase();
      const productKey = `${String(choice?.__sourceQuestionId || 'product')}_${String(getChoiceDisplay(choice)).trim().toLowerCase()}`;

      if (!treatmentGroups.has(treatmentKey)) {
        treatmentGroups.set(treatmentKey, {
          type: 'treatment_group',
          key: `treatment_group_${treatmentKey}`,
          title:
            choice?.treatment ||
            choice?.category_name ||
            choice?.treatment_type_name ||
            choice?.treatment_type_key ||
            'Treatment',
          eyebrow:
            choice?.__sourceQuestionText ||
            choice?.treatment_type_name ||
            choice?.treatment_type_key ||
            'Treatment options',
          products: [],
          productMap: new Map(),
          firstIndex: index,
        });
      }

      const treatmentGroup = treatmentGroups.get(treatmentKey);
      if (!treatmentGroup.productMap.has(productKey)) {
        const productGroup = {
          type: 'product_group',
          key: `product_group_${productKey}`,
          title: getChoiceDisplay(choice),
          choices: [],
          firstIndex: index,
        };
        treatmentGroup.productMap.set(productKey, productGroup);
        treatmentGroup.products.push(productGroup);
      }

      treatmentGroup.productMap.get(productKey).choices.push(choice);
    });

    treatmentGroups.forEach((group) => {
      rows.push({
        ...group,
        products: group.products.sort((a, b) => a.firstIndex - b.firstIndex),
      });
    });

    return rows.sort((a, b) => {
      const aIndex = a.firstIndex ?? (Number(String(a.key).split('_').pop()) || 0);
      const bIndex = b.firstIndex ?? (Number(String(b.key).split('_').pop()) || 0);
      return aIndex - bIndex;
    });
  })();

  return (
    <Container>
      <OptionsContainer>
        {groupedChoiceRows.map((row, index) => {
          if (row.type === 'treatment_group') {
            return (
              <TreatmentGroupCard key={row.key}>
                <TreatmentGroupHeader>
                  <TreatmentGroupEyebrow>{row.eyebrow}</TreatmentGroupEyebrow>
                  <TreatmentGroupTitle>{row.title}</TreatmentGroupTitle>
                </TreatmentGroupHeader>

                <TreatmentProductsStack>
                  {row.products.map((productGroup) => {
                    const selectedChoice = getSelectedChoiceInGroup(productGroup.choices);
                    const displayChoice = selectedChoice || productGroup.choices[0];
                    const displaySub = displayChoice?.description || displayChoice?.category_name || displayChoice?.titration_category || '';
                    const selectedChoiceValue = selectedChoice ? getChoiceValue(selectedChoice) : null;
                    const selectedPricing = selectedChoice ? getChoicePricing(selectedChoice) : null;
                    const selectedLinkedSupplies = selectedChoice ? getLinkedSupplies(selectedChoice) : [];
                    const selectedSuppliesAddons = getSuppliesAddonsTotal(selectedLinkedSupplies);
                    const selectedTotalAmount = selectedPricing?.total ?? selectedPricing?.finalPrice ?? 0;
                    const selectedMedicationAmount = Math.max(0, (selectedPricing?.finalPrice ?? 0) - selectedSuppliesAddons);

                    return (
                      <ProgramGroupCard key={productGroup.key}>
                        <ProgramGroupTitle>{productGroup.title}</ProgramGroupTitle>
                        {displaySub && <ProgramGroupSub>{displaySub}</ProgramGroupSub>}
                        <ProgramGroupPrompt>Choose your supply</ProgramGroupPrompt>
                        <ProgramOptionsGrid>
                          {productGroup.choices.map((choice) => {
                            const choiceValue = getChoiceValue(choice);
                            const pricing = getChoicePricing(choice);
                            const isSelected = isChoiceSelected(choice);
                            const totalAmount = pricing?.total ?? pricing?.finalPrice ?? 0;
                            const daysSupply = Number(choice?.days_supply || 0);
                            const label = daysSupply ? `${daysSupply}-day supply` : "Program";
                            const monthlyEquivalent = daysSupply > 30 && totalAmount > 0
                              ? formatPrice((totalAmount / daysSupply) * 30)
                              : null;

                            return (
                              <ProgramOptionButton
                                key={choiceValue}
                                type="button"
                                $isSelected={isSelected}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleChange(selectProductProgram(choice, productGroup.choices));
                                }}
                              >
                                {daysSupply >= 45 && <ProgramOptionBadge>Save</ProgramOptionBadge>}
                                <ProgramOptionTop>
                                  <ProgramOptionDot $isSelected={isSelected} />
                                  {label}
                                </ProgramOptionTop>
                                {pricing && (
                                  <>
                                    <ProgramOptionPrice>{formatPrice(totalAmount)}</ProgramOptionPrice>
                                    <ProgramOptionMeta>
                                      {monthlyEquivalent ? `${monthlyEquivalent}/mo equivalent` : "Billed monthly"}
                                    </ProgramOptionMeta>
                                  </>
                                )}
                              </ProgramOptionButton>
                            );
                          })}
                        </ProgramOptionsGrid>

                        {selectedChoice && selectedPricing && (
                          <ReceiptContainer>
                            <ReceiptRow>
                              <ReceiptLeft>
                                <ReceiptLabel>Medication Base</ReceiptLabel>
                                {selectedChoice?.days_supply && <ReceiptMeta>{selectedChoice.days_supply} day program</ReceiptMeta>}
                              </ReceiptLeft>
                              <ReceiptRight>
                                {selectedPricing.hasDiscount && selectedPricing.originalPrice != null && (
                                  <ReceiptOriginalPrice>{formatPrice(selectedPricing.originalPrice)}</ReceiptOriginalPrice>
                                )}
                                <span>{formatPrice(selectedMedicationAmount)}</span>
                              </ReceiptRight>
                            </ReceiptRow>

                            {selectedLinkedSupplies.map((supply, supplyIndex) => {
                              const name = supply?.name || "Supply";
                              const qty = Number(supply?.quantity || 1);
                              const included = Boolean(supply?.is_included);
                              const unitPriceRaw =
                                supply?.unit_price !== undefined && supply?.unit_price !== null
                                  ? Number(supply.unit_price)
                                  : null;
                              const rowTotal = included ? 0 : (unitPriceRaw !== null ? unitPriceRaw * qty : null);

                              return (
                                <ReceiptRow key={`${selectedChoiceValue}_supply_${supplyIndex}`}>
                                  <ReceiptLeft>
                                    <ReceiptLabel>{name}</ReceiptLabel>
                                    <ReceiptMeta>
                                      x{qty}
                                      <SupplyBadge $included={included}>
                                        {included ? "Included" : "Billed separately"}
                                      </SupplyBadge>
                                    </ReceiptMeta>
                                  </ReceiptLeft>
                                  <ReceiptRight>
                                    {included ? "Included (no extra charge)" : (rowTotal !== null ? `+ ${formatPrice(rowTotal)} billed separately` : "Billed separately")}
                                  </ReceiptRight>
                                </ReceiptRow>
                              );
                            })}

                            {(selectedPricing.shippingFee > 0 || selectedLinkedSupplies.some(s => s?.shipping_fee > 0)) && (
                              <ReceiptRow>
                                <ReceiptLeft>
                                  <ReceiptLabel>Service & Delivery</ReceiptLabel>
                                </ReceiptLeft>
                                <ReceiptRight>
                                  + {formatPrice(selectedPricing.shippingFee || 0)}
                                </ReceiptRight>
                              </ReceiptRow>
                            )}

                            <ReceiptRow $isTotal>
                              <ReceiptLeft>
                                <ReceiptLabel>Total Today</ReceiptLabel>
                              </ReceiptLeft>
                              <ReceiptRight>
                                {formatPrice(selectedTotalAmount)}
                              </ReceiptRight>
                            </ReceiptRow>
                          </ReceiptContainer>
                        )}
                      </ProgramGroupCard>
                    );
                  })}
                </TreatmentProductsStack>
              </TreatmentGroupCard>
            );
          }

          if (row.type === 'product_group') {
            const selectedChoice = getSelectedChoiceInGroup(row.choices);
            const displayChoice = selectedChoice || row.choices[0];
            const displaySub = displayChoice?.description || displayChoice?.category_name || displayChoice?.titration_category || '';
            const selectedChoiceValue = selectedChoice ? getChoiceValue(selectedChoice) : null;
            const selectedPricing = selectedChoice ? getChoicePricing(selectedChoice) : null;
            const selectedLinkedSupplies = selectedChoice ? getLinkedSupplies(selectedChoice) : [];
            const selectedSuppliesAddons = getSuppliesAddonsTotal(selectedLinkedSupplies);
            const selectedTotalAmount = selectedPricing?.total ?? selectedPricing?.finalPrice ?? 0;
            const selectedMedicationAmount = Math.max(0, (selectedPricing?.finalPrice ?? 0) - selectedSuppliesAddons);

            return (
              <ProgramGroupCard key={row.key}>
                <ProgramGroupEyebrow>
                  {displayChoice?.category_name || displayChoice?.treatment || "Choose Program"}
                </ProgramGroupEyebrow>
                <ProgramGroupTitle>{row.title}</ProgramGroupTitle>
                {displaySub && <ProgramGroupSub>{displaySub}</ProgramGroupSub>}
                <ProgramGroupPrompt>Choose your supply</ProgramGroupPrompt>
                <ProgramOptionsGrid>
                  {row.choices.map((choice) => {
                    const choiceValue = getChoiceValue(choice);
                    const pricing = getChoicePricing(choice);
                    const isSelected = isChoiceSelected(choice);
                    const totalAmount = pricing?.total ?? pricing?.finalPrice ?? 0;
                    const daysSupply = Number(choice?.days_supply || 0);
                    const label = daysSupply ? `${daysSupply}-day supply` : "Program";
                    const monthlyEquivalent = daysSupply > 30 && totalAmount > 0
                      ? formatPrice((totalAmount / daysSupply) * 30)
                      : null;

                    return (
                      <ProgramOptionButton
                        key={choiceValue}
                        type="button"
                        $isSelected={isSelected}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleChange(selectProductProgram(choice, row.choices));
                        }}
                      >
                        {daysSupply >= 45 && <ProgramOptionBadge>Save</ProgramOptionBadge>}
                        <ProgramOptionTop>
                          <ProgramOptionDot $isSelected={isSelected} />
                          {label}
                        </ProgramOptionTop>
                        {pricing && (
                          <>
                            <ProgramOptionPrice>{formatPrice(totalAmount)}</ProgramOptionPrice>
                            <ProgramOptionMeta>
                              {monthlyEquivalent ? `${monthlyEquivalent}/mo equivalent` : "Billed monthly"}
                            </ProgramOptionMeta>
                          </>
                        )}
                      </ProgramOptionButton>
                    );
                  })}
                </ProgramOptionsGrid>

                {selectedChoice && selectedPricing && (
                  <ReceiptContainer>
                    <ReceiptRow>
                      <ReceiptLeft>
                        <ReceiptLabel>Medication Base</ReceiptLabel>
                        {selectedChoice?.days_supply && <ReceiptMeta>{selectedChoice.days_supply} day program</ReceiptMeta>}
                      </ReceiptLeft>
                      <ReceiptRight>
                        {selectedPricing.hasDiscount && selectedPricing.originalPrice != null && (
                          <ReceiptOriginalPrice>{formatPrice(selectedPricing.originalPrice)}</ReceiptOriginalPrice>
                        )}
                        <span>{formatPrice(selectedMedicationAmount)}</span>
                      </ReceiptRight>
                    </ReceiptRow>

                    {selectedLinkedSupplies.map((supply, supplyIndex) => {
                      const name = supply?.name || "Supply";
                      const qty = Number(supply?.quantity || 1);
                      const included = Boolean(supply?.is_included);
                      const unitPriceRaw =
                        supply?.unit_price !== undefined && supply?.unit_price !== null
                          ? Number(supply.unit_price)
                          : null;
                      const rowTotal = included ? 0 : (unitPriceRaw !== null ? unitPriceRaw * qty : null);

                      return (
                        <ReceiptRow key={`${selectedChoiceValue}_supply_${supplyIndex}`}>
                          <ReceiptLeft>
                            <ReceiptLabel>{name}</ReceiptLabel>
                            <ReceiptMeta>
                              x{qty}
                              <SupplyBadge $included={included}>
                                {included ? "Included" : "Billed separately"}
                              </SupplyBadge>
                            </ReceiptMeta>
                          </ReceiptLeft>
                          <ReceiptRight>
                            {included ? "Included (no extra charge)" : (rowTotal !== null ? `+ ${formatPrice(rowTotal)} billed separately` : "Billed separately")}
                          </ReceiptRight>
                        </ReceiptRow>
                      );
                    })}

                    {(selectedPricing.shippingFee > 0 || selectedLinkedSupplies.some(s => s?.shipping_fee > 0)) && (
                      <ReceiptRow>
                        <ReceiptLeft>
                          <ReceiptLabel>Service & Delivery</ReceiptLabel>
                        </ReceiptLeft>
                        <ReceiptRight>
                          + {formatPrice(selectedPricing.shippingFee || 0)}
                        </ReceiptRight>
                      </ReceiptRow>
                    )}

                    <ReceiptRow $isTotal>
                      <ReceiptLeft>
                        <ReceiptLabel>Total Today</ReceiptLabel>
                      </ReceiptLeft>
                      <ReceiptRight>
                        {formatPrice(selectedTotalAmount)}
                      </ReceiptRight>
                    </ReceiptRow>
                  </ReceiptContainer>
                )}
              </ProgramGroupCard>
            );
          }

          const choice = row.choice;
          const choiceValue = getChoiceValue(choice);
          const displayText = getChoiceDisplay(choice);
          const isSelected = isChoiceSelected(choice);
          const pricing = getChoicePricing(choice);
          const linkedSupplies = getLinkedSupplies(choice);
          const imageUrl = getChoiceImage(choice);
          const showImage = Boolean(imageUrl) && !imageLoadFailures[choiceValue];

          const isProductCard = typeof choice === 'object' && choice !== null && Boolean(choice.product_id || choice.id);
          const totalAmount = pricing?.total ?? pricing?.finalPrice ?? 0;
          const suppliesAddons = getSuppliesAddonsTotal(linkedSupplies);
          const medicationAmount = Math.max(0, (pricing?.finalPrice ?? 0) - suppliesAddons);

          return (
            <OptionBlock key={index}>
              <input
                type={isMultiProductSelection ? "checkbox" : "radio"}
                name={`question_${question.id}`}
                value={choiceValue}
                checked={isSelected}
                onChange={() =>
                  isMultiProductSelection
                    ? handleChange(toggleMultiSelection(choice))
                    : handleChange(
                        typeof choice === 'string'
                          ? choice
                          : choice.product_id || choice.id
                            ? choice
                            : choiceValue
                      )
                }
                id={`${question.id}_${index}`}
              />
              {isProductCard ? (
                <ProductCard
                  htmlFor={`${question.id}_${index}`}
                  $isSelected={isSelected}
                  onClick={(e) =>
                    handleLabelClick(
                      e,
                      typeof choice === 'string'
                        ? choice
                        : choice.product_id || choice.id
                          ? choice
                          : choiceValue
                    )
                  }
                >
                  <ProductHeader>
                    <ProductMain>
                      <SelectionIndicator $isSelected={isSelected}>
                        <CheckIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </CheckIcon>
                      </SelectionIndicator>
                      {showImage && (
                        <ProductImageWrap>
                          <ProductImage
                            src={imageUrl}
                            alt={displayText}
                            onError={() =>
                              setImageLoadFailures((prev) => ({ ...prev, [choiceValue]: true }))
                            }
                          />
                        </ProductImageWrap>
                      )}
                      <ProductTitleWrap>
                        <ProductTitle>{displayText}</ProductTitle>
                        <ProductSub>
                          {choice?.days_supply != null ? `${choice.days_supply} days supply` : "Days Supply: —"}
                          {choice?.refills != null && ` • Refills: ${choice.refills}`}
                        </ProductSub>
                      </ProductTitleWrap>
                    </ProductMain>
                    {pricing && (
                      <ProductPriceWrap>
                        <ProductPrice>{formatPrice(totalAmount)}</ProductPrice>
                      </ProductPriceWrap>
                    )}
                  </ProductHeader>

                  {pricing && (
                    <DetailsToggle 
                      $isSelected={isSelected}
                      onClick={(e) => toggleCard(e, choiceValue)}
                    >
                      {expandedCards[choiceValue] ? "Hide Breakdown" : "View Breakdown"}
                      <ChevronDown $isExpanded={expandedCards[choiceValue]} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </ChevronDown>
                    </DetailsToggle>
                  )}

                  {pricing && expandedCards[choiceValue] && (
                    <ReceiptContainer>
                      <ReceiptRow>
                        <ReceiptLeft>
                          <ReceiptLabel>Medication Base</ReceiptLabel>
                          <ReceiptMeta>
                            {choice?.days_supply != null ? `${choice.days_supply} days supply` : "Days Supply: —"}
                            {choice?.refills != null && ` • Refills: ${choice.refills}`}
                          </ReceiptMeta>
                        </ReceiptLeft>
                        <ReceiptRight>
                          {pricing.hasDiscount && pricing.originalPrice != null && (
                            <ReceiptOriginalPrice>{formatPrice(pricing.originalPrice)}</ReceiptOriginalPrice>
                          )}
                          <span>{formatPrice(medicationAmount)}</span>
                        </ReceiptRight>
                      </ReceiptRow>

                      {linkedSupplies.map((supply, supplyIndex) => {
                        const name = supply?.name || "Supply";
                        const qty = Number(supply?.quantity || 1);
                        const included = Boolean(supply?.is_included);
                        const unitPriceRaw =
                          supply?.unit_price !== undefined && supply?.unit_price !== null
                            ? Number(supply.unit_price)
                            : null;

                        const rowTotal = included ? 0 : (unitPriceRaw !== null ? unitPriceRaw * qty : null);

                        return (
                          <ReceiptRow key={`supply_${supplyIndex}`}>
                            <ReceiptLeft>
                              <ReceiptLabel>{name}</ReceiptLabel>
                              <ReceiptMeta>
                                x{qty}
                                <SupplyBadge $included={included}>
                                  {included ? "Included" : "Billed separately"}
                                </SupplyBadge>
                              </ReceiptMeta>
                            </ReceiptLeft>
                            <ReceiptRight>
                              {included ? "Included (no extra charge)" : (rowTotal !== null ? `+ ${formatPrice(rowTotal)} billed separately` : "Billed separately")}
                            </ReceiptRight>
                          </ReceiptRow>
                        );
                      })}

                      {(pricing.shippingFee > 0 || linkedSupplies.some(s => s?.shipping_fee > 0)) && (
                        <ReceiptRow>
                          <ReceiptLeft>
                            <ReceiptLabel>Service & Delivery</ReceiptLabel>
                          </ReceiptLeft>
                          <ReceiptRight>
                            + {formatPrice(pricing.shippingFee || 0)}
                          </ReceiptRight>
                        </ReceiptRow>
                      )}

                      <ReceiptRow $isTotal>
                        <ReceiptLeft>
                          <ReceiptLabel>Total Today</ReceiptLabel>
                        </ReceiptLeft>
                        <ReceiptRight>
                          {formatPrice(totalAmount)}
                        </ReceiptRight>
                      </ReceiptRow>
                    </ReceiptContainer>
                  )}
                </ProductCard>
              ) : (
                <OptionLabel
                  htmlFor={`${question.id}_${index}`}
                  $isSelected={isSelected}
                  onClick={(e) =>
                    handleLabelClick(
                      e,
                      typeof choice === 'string'
                        ? choice
                        : choice.product_id || choice.id
                          ? choice
                          : choiceValue
                    )
                  }
                >
                  <OptionText>{displayText}</OptionText>
                  {pricing && (
                    <PriceContainer>
                      {pricing.hasDiscount && pricing.originalPrice != null && (
                        <PriceOriginal>{formatPrice(pricing.originalPrice)}</PriceOriginal>
                      )}
                      {pricing.total != null ? (
                        <PriceFinal>{formatPrice(pricing.total)}</PriceFinal>
                      ) : pricing.finalPrice != null ? (
                        <PriceFinal>{formatPrice(pricing.finalPrice)}</PriceFinal>
                      ) : null}
                      {pricing.shippingFee > 0 && (
                        <PriceShipping>+ {formatPrice(pricing.shippingFee)} shipping</PriceShipping>
                      )}
                    </PriceContainer>
                  )}
                </OptionLabel>
              )}
            </OptionBlock>
          );
        })}
      </OptionsContainer>

      {error && (
        <ErrorCard
          message={error}
          variant="error"
        />
      )}
    </Container>
  );
};

export default SingleChoice;
